import { useQuery, keepPreviousData, useMutation, UseQueryOptions } from '@tanstack/react-query'
import { Role, RoleBase } from '../../shared/types'
import { del, get, post, put } from '../fetch'
import { ParamsType } from '../scaffolding/types'

const staleTime = 60 * 60 * 1000

export type AccountRolesParamsType = {
	id?: string
	name?: string
	licensePlate?: string
}

export function useAccountRoleList(searchParams: ParamsType & AccountRolesParamsType) {
	return useQuery({
		queryKey: ['api/account-roles', searchParams],
		queryFn: () =>
			post<{ total: number; list: RoleBase[] }>(`/api/0/account-roles`, searchParams),
		placeholderData: keepPreviousData,
		staleTime,
	})
}

export function useAccountRoleListAll<T = RoleBase[]>(
	options?: Omit<UseQueryOptions<RoleBase[], Error, T>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		...options,
		queryKey: ['api/account-roles'],
		queryFn: () => get<RoleBase[]>(`/api/0/account-roles`),
		staleTime,
	})
}

export function useAccountRoleGet(id: string) {
	return useQuery({
		queryKey: ['api/account-roles', id],
		queryFn: () => get<Role>(`/api/0/account-roles/${id}`),
		staleTime,
	})
}

export function useAccountRoleCreate() {
	return useMutation({
		mutationFn: (data: FormData) => put('/api/0/account-roles', data),
		meta: {
			invalidateQueries: [['api/account-roles']],
			notifications: {
				success: () => 'Role vytvořena',
			},
		},
	})
}

export function useAccountRoleUpdate(id: string) {
	return useMutation({
		mutationFn: (data: FormData) => post(`/api/0/account-roles/${id}`, data),
		meta: {
			invalidateQueries: [
				['api/account-roles'],
				['api/user'],
				['api/me', 'account-permissions'],
			],
			notifications: {
				success: () => 'Změny uloženy',
			},
		},
	})
}

export function useAccountRoleRemove() {
	return useMutation({
		mutationFn: (id: string) => del(`/api/0/account-roles/${id}`),
		meta: {
			invalidateQueries: [
				['api/account-roles'],
				['api/user'],
				['api/me', 'account-permissions'],
			],
			notifications: {
				success: () => 'Role smazána',
			},
		},
	})
}
